import { listArtifactTypes, type ArtifactRegistry } from '../../../../src/artifacts/registry.mjs';
import type { LibraryKindGroups } from './library.ts';

/** DES1's accepted display groups, including retained nonauthoring Results. */
export const PR1_LIBRARY_KIND_GROUPS: LibraryKindGroups = Object.freeze({
  all: Object.freeze([
    'report',
    'page',
    'site',
    'deck',
    'document',
    'spreadsheet',
    'dashboard',
    'table',
    'chart',
    'markdown',
    'kpi',
  ] as const),
  reports: Object.freeze(['report', 'page', 'site', 'dashboard', 'chart', 'kpi'] as const),
  decks: Object.freeze(['deck'] as const),
  documents: Object.freeze(['document', 'markdown'] as const),
  spreadsheets: Object.freeze(['spreadsheet', 'table'] as const),
});

/**
 * The groups for a host whose registry carries the registered App descriptor (PR2): the same
 * PR1 groups, `all` ending with the App kind, and one more filter, Apps. Without that descriptor
 * this is exactly PR1's groups, so PR1 never offers or queries an App.
 */
export function libraryKindGroups(registry?: ArtifactRegistry): LibraryKindGroups {
  if (!registry?.list().some((type) => type.id === 'app')) return PR1_LIBRARY_KIND_GROUPS;
  return Object.freeze({
    ...PR1_LIBRARY_KIND_GROUPS,
    all: Object.freeze([...PR1_LIBRARY_KIND_GROUPS.all, 'app'] as const),
    apps: Object.freeze(['app'] as const),
  });
}

export function assertLibraryGroups(groups: LibraryKindGroups, registry?: ArtifactRegistry) {
  const registered: string[] = (registry ? registry.list() : listArtifactTypes()).map(
    (type) => type.id,
  );
  if (
    groups.all.length !== registered.length ||
    new Set(groups.all).size !== groups.all.length ||
    registered.some((kind) => !(groups.all as readonly string[]).includes(kind))
  )
    throw new TypeError('library_kind_group_incomplete');
  const grouped = ['reports', 'decks', 'documents', 'spreadsheets', 'apps'].flatMap(
    (group) => groups[group as Exclude<keyof LibraryKindGroups, 'all'>] ?? [],
  );
  if (
    grouped.length !== registered.length ||
    new Set(grouped).size !== registered.length ||
    grouped.some((kind) => !groups.all.includes(kind))
  )
    throw new TypeError('library_kind_group_invalid');
}
